'use client';

import Paginator from '@/app/_components/paginator';
import CardV2, { AlgoliaCardProps } from '@/app/_components/widgets/listing/cardV2';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { getAlgoliaSearchResponse, SortingListingType } from '@/api/algolia';
import { useIsWelfare } from '@/app/_hooks/useIsWelfare';

const CARDS_PER_PAGE = 10;

export const handleServiceSelect = (service: string, searchParams: URLSearchParams | null) => {
  // Update URL with selected service
  const params = new URLSearchParams(searchParams?.toString() ?? '');
  params.set('search', service);
  params.delete('page');
  if (service) {
    params.delete('speciality');
    params.delete('specialityId');
    params.delete('doctorId');
  }
  window.history.pushState(null, '', `?${params.toString()}`);
};

export const useSearchState = () => {
  const [inputValue, setInputValue] = useState('');
  const searchParams = useSearchParams();
  const search = searchParams?.get('search') ?? '';

  useEffect(() => {
    setInputValue(search);
  }, [search]);

  const selectedService = searchParams?.get('search') ?? '';

  return {
    inputValue,
    setInputValue,
    selectedService,
    searchParams,
  };
};

const useAlgoliaSearch = (ambulatoryId: string, searchHealthcareService: string, searchPage: number, isWelfare: boolean) => {
  const [data, setData] = useState<{ hits: AlgoliaCardProps[]; pages: number }>({ hits: [], pages: 0 });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const searchResponse = await getAlgoliaSearchResponse({
        text: searchHealthcareService,
        page: searchPage,
        order: SortingListingType.noDistinct,
        filterObj: { ambulatoryId, excludePrice0: isWelfare },
        hitsPerPage: CARDS_PER_PAGE,
      });
      setData({ hits: searchResponse.hits, pages: searchResponse.nbPages });
      setIsLoading(false);
    };

    if (searchHealthcareService && ambulatoryId) {
      fetchData();
    }
  }, [ambulatoryId, searchHealthcareService, searchPage, isWelfare]);

  return { data, isLoading };
};

export const ProfileListing: React.FC<{ items: AlgoliaCardProps[] }> = ({ items: _items }) => {
  const isWelfare = useIsWelfare();
  // Exclude services with price 0 if user is welfare
  const items = useMemo(
    () =>
      _items.filter(item => {
        // hide services with price 0 if user is welfare
        if (isWelfare && item.specializations?.doctors?.some(d => d.price === 0)) return false;
        return true;
      }),
    [_items, isWelfare],
  );

  const params = useSearchParams();
  const parsedPage = Number(params?.get('page') ?? '1');
  const currentPage = isNaN(parsedPage) ? 1 : parsedPage;
  // Custom hooks for search state and suggestions
  const { selectedService } = useSearchState();
  // Algolia search results for selected service
  const { data: algoliaSearchData, isLoading: isAlgoliaSearchLoading } = useAlgoliaSearch(
    items[0]?.ambulatoryId,
    selectedService, // Only search when a service is selected
    currentPage - 1,
    isWelfare,
  );

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (selectedService && algoliaSearchData) return algoliaSearchData.pages;
    return Math.ceil(items.length / CARDS_PER_PAGE);
  }, [selectedService, algoliaSearchData, items.length]);

  const doctorId = params?.get('doctorId');
  const specialityId = params?.get('specialityId');

  const paramsCard = useMemo(() => {
    const item = items.find(item => item.specializations?.id === specialityId);
    // If specialityId is not found, return null
    if (!item) return null;
    if (doctorId) {
      // If doctorId is provided, return the specialization with the doctor
      const doctor = item.specializations?.doctors?.find(doctor => doctor?.id === doctorId);
      if (doctor) return { ...item, specializations: { ...item.specializations, doctors: [doctor] } };
      else console.error('Doctor not found');
    }

    // If doctorId is not provided or not found, return the item with default doctors
    return item;
  }, [doctorId, items, specialityId]);

  // Determine which cards to show
  const cardsToShow = useMemo(() => {
    if (isAlgoliaSearchLoading && selectedService) return [];
    if (selectedService) return algoliaSearchData.hits;

    return items.slice((currentPage - 1) * CARDS_PER_PAGE, currentPage * CARDS_PER_PAGE);
  }, [algoliaSearchData.hits, currentPage, isAlgoliaSearchLoading, items, selectedService]);

  return (
    <div className="flex flex-col gap-4 py-4" id="profile-listing-container">
      {paramsCard && (
        <>
          <CardV2 item={paramsCard} showAllServicesEnabled={false} showPhoneNumber={false} showNoAvailabilitiesCallToAction={false} />
          <div className="h-[1px] w-full bg-neutral-100" />
        </>
      )}
      {cardsToShow.map(item => {
        const isAlgolia = selectedService && algoliaSearchData;
        const objectID = item.objectID.split(':');
        // The objectID is like this: "ambulatoryId:doctorId:specialityId
        const originalDoctorId = isAlgolia && objectID.length === 3 ? objectID[1] : undefined;
        return (
          <CardV2
            item={item}
            showNoAvailabilitiesCallToAction={false}
            key={`${item.objectID}`}
            showAllServicesEnabled={false}
            showPhoneNumber={false}
            originalDoctorId={originalDoctorId}
          />
        );
      })}

      <Paginator totalPages={totalPages} />
    </div>
  );
};
